(() => {
  document.documentElement.classList.add("js");

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const isSafePath = (value) => {
    if (typeof value !== "string") return false;
    const raw = value.trim();
    if (!raw || raw.length > 500) return false;
    if (raw.includes("\\") || raw.includes("://") || /[<>"'`\s]/.test(raw)) return false;
    if (/javascript:|data:|vbscript:/i.test(raw)) return false;
    if (raw.startsWith("/") && !raw.startsWith("//")) return true;
    if (raw.startsWith("./") && !raw.includes("..")) return true;
    return false;
  };

  const safeStoreHref = (value, fallback = "#") => (isSafePath(value) ? value.trim() : fallback);

  const isAllowedMedia = (value) => {
    if (!value || typeof value !== "string") return false;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== "https:" && url.protocol !== "http:") return false;
      const host = url.hostname.toLowerCase();
      const here = window.location.hostname.toLowerCase();
      if (host === here) return true;
      return (
        host === "cdn.shopify.com" ||
        host.endsWith(".shopifycdn.com") ||
        host.endsWith(".shopify.com") ||
        host.endsWith(".myshopify.com")
      );
    } catch (e) {
      return false;
    }
  };

  const safeSrcset = (value) => {
    if (!value) return "";
    const parts = String(value)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const out = [];
    for (const part of parts) {
      const bits = part.split(/\s+/);
      const src = bits[0];
      const desc = bits.slice(1).join(" ");
      if (!isAllowedMedia(src)) return "";
      if (desc && !/^\d+[wx]$/.test(desc)) return "";
      out.push(desc ? `${src} ${desc}` : src);
    }
    return out.join(", ");
  };

  const isCartKey = (value) => typeof value === "string" && /^[A-Za-z0-9:._-]{1,200}$/.test(value);

  const parseVariantId = (raw) => {
    const n = Number(String(raw ?? "").trim());
    if (!Number.isSafeInteger(n) || n <= 0) return null;
    return n;
  };

  const parseQty = (raw, fallback = 1) => {
    const n = Number(raw);
    if (!Number.isSafeInteger(n)) return fallback;
    return Math.max(0, Math.min(99, n));
  };

  const header = document.querySelector(".site-header");
  if (header) {
    const onScroll = () => header.classList.toggle("is-stuck", window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  const toggle = document.querySelector("[data-nav-toggle]");
  const nav = document.querySelector("[data-nav]");
  if (toggle && nav) {
    toggle.addEventListener("click", () => nav.classList.toggle("is-open"));
    nav.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => nav.classList.remove("is-open"));
    });
  }

  document.querySelectorAll("[data-variant]").forEach((input) => {
    input.addEventListener("change", () => {
      const hidden = document.querySelector("[data-variant-id]");
      const id = parseVariantId(input.value);
      if (hidden && input.checked && id) hidden.value = String(id);
    });
  });

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const marquee = document.querySelector("[data-marquee]");
  if (marquee && !reduceMotion) {
    const parent = marquee.parentElement;
    const unit = [...marquee.children].map((n) => n.cloneNode(true));
    const fill = () => {
      if (!parent || !unit.length) return;
      const need = parent.offsetWidth * 2 + 80;
      if (!parent.offsetWidth) return;
      marquee.replaceChildren();
      let copies = 0;
      while (marquee.scrollWidth < need || copies < 2) {
        unit.forEach((n) => marquee.appendChild(n.cloneNode(true)));
        copies += 1;
        if (copies > 24) break;
      }
      if (copies % 2) unit.forEach((n) => marquee.appendChild(n.cloneNode(true)));
      marquee.style.animation = "none";
      void marquee.offsetWidth;
      marquee.style.animation = "";
      marquee.style.animationDuration = `${Math.max(24, marquee.scrollWidth / 70)}s`;
    };
    fill();
    window.addEventListener("resize", fill);
  }

  const heroMedia = document.querySelector(".hero img, .hero__media");
  if (heroMedia && !reduceMotion) {
    const onHero = () => {
      const y = window.scrollY;
      if (y > window.innerHeight) return;
      heroMedia.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
    };
    onHero();
    window.addEventListener("scroll", onHero, { passive: true });
  }

  const stage = document.querySelector(".product__stage");
  const thumbs = [...document.querySelectorAll("[data-product-thumb]")];
  if (stage && thumbs.length) {
    let front = stage.querySelector("img");
    const warm = (btn) => {
      if (!btn || !btn.dataset.full || btn.dataset.warmed) return;
      if (!isAllowedMedia(btn.dataset.full)) return;
      btn.dataset.warmed = "1";
      const pre = new Image();
      const srcset = safeSrcset(btn.dataset.srcset);
      if (srcset) pre.srcset = srcset;
      pre.src = btn.dataset.full;
    };
    thumbs.forEach((btn) => {
      btn.addEventListener("pointerenter", () => warm(btn), { once: true });
    });

    thumbs.forEach((btn) => {
      btn.addEventListener("click", () => {
        const next = btn.dataset.full;
        if (!next || !front || !isAllowedMedia(next)) return;
        thumbs.forEach((other) => other.classList.toggle("is-active", other === btn));
        if (front.getAttribute("src") === next) return;

        stage.querySelectorAll("img.is-next").forEach((n) => n.remove());

        const incoming = front.cloneNode(false);
        incoming.classList.add("is-next");
        incoming.alt = btn.dataset.alt || "";
        incoming.style.opacity = "0";
        const srcset = safeSrcset(btn.dataset.srcset);
        if (srcset) incoming.srcset = srcset;
        else incoming.removeAttribute("srcset");
        incoming.src = next;
        stage.appendChild(incoming);

        const reveal = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              incoming.style.opacity = "1";
            });
          });
          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            if (front && front.parentNode) front.remove();
            incoming.classList.remove("is-next");
            front = incoming;
          };
          incoming.addEventListener("transitionend", done, { once: true });
          window.setTimeout(done, 480);
        };

        if (incoming.complete) reveal();
        else {
          incoming.addEventListener("load", reveal, { once: true });
          incoming.addEventListener("error", () => incoming.remove(), { once: true });
        }
      });
    });
  }

  const splitProductStory = () => {
    const story = document.querySelector(".product__story");
    const buy = document.querySelector(".product__buy");
    if (!story || !buy) return;

    const isWrapper = (el) => {
      if (el.tagName !== "DIV") return false;
      const hasText = [...el.childNodes].some((n) => n.nodeType === 3 && n.textContent.trim());
      return !hasText && el.children.length > 0;
    };

    const blocks = [];
    const visit = (node) => {
      [...node.children].forEach((child) => {
        if (isWrapper(child)) visit(child);
        else blocks.push(child);
      });
    };
    visit(story);

    const meaningful = blocks.filter((n) => n.textContent && n.textContent.trim());
    if (!meaningful.length) return;

    const lead = document.createElement("div");
    lead.className = "product__lead";
    lead.appendChild(meaningful[0]);
    story.replaceChildren(lead);
    story.setAttribute("data-split", "");

    const rest = meaningful.slice(1).filter((n) => n.tagName !== "HR");
    if (!rest.length) return;

    const fold = document.createElement("details");
    fold.className = "product__fold";
    const summary = document.createElement("summary");
    summary.textContent = story.dataset.detailsLabel || "Details";
    const body = document.createElement("div");
    body.className = "product__fold-body";
    rest.forEach((n) => body.appendChild(n));
    fold.append(summary, body);

    const form = buy.querySelector("form");
    if (form) form.after(fold);
    else buy.appendChild(fold);
  };
  splitProductStory();

  const sizeChart = document.querySelector("[data-size-chart]");
  if (sizeChart) {
    const setChart = (open) => {
      sizeChart.hidden = !open;
      document.body.classList.toggle("is-chart-open", open);
      if (open) {
        const closeBtn = sizeChart.querySelector(".size-chart__close");
        if (closeBtn) closeBtn.focus();
      }
    };
    document.querySelectorAll("[data-size-chart-open]").forEach((btn) => {
      btn.addEventListener("click", () => setChart(true));
    });
    sizeChart.querySelectorAll("[data-size-chart-close]").forEach((btn) => {
      btn.addEventListener("click", () => setChart(false));
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !sizeChart.hidden) setChart(false);
    });
  }

  const playReel = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    const run = () => video.play().catch(() => {});
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) run();
          else video.pause();
        });
      }, { threshold: 0.15 });
      io.observe(video);
    } else {
      run();
    }
  };

  document.querySelectorAll(".film__reel video, video[autoplay]").forEach(playReel);

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) return;
    document.querySelectorAll(".film__reel video, video[autoplay]").forEach((video) => {
      video.play().catch(() => {});
    });
  });

  const reveal = document.querySelectorAll(".reveal");
  if (reveal.length && "IntersectionObserver" in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("in");
        io.unobserve(entry.target);
      });
    }, { rootMargin: "240px 0px", threshold: 0.01 });
    reveal.forEach((el) => {
      const box = el.getBoundingClientRect();
      if (box.top < window.innerHeight + 80) el.classList.add("in");
      else io.observe(el);
    });
  } else {
    reveal.forEach((el) => el.classList.add("in"));
  }

  const cartRoot = () => {
    const cfgEl = document.querySelector("[data-cart-config]");
    let cfg = {
      cart: { items: [], item_count: 0, total_price: 0 },
      moneyFormat: "${{amount}}",
      cartUrl: "/cart",
      checkoutUrl: "/checkout",
      shopUrl: "/collections/all",
      shop: Boolean(window.Shopify),
      strings: {
        bag: "Bag",
        empty: "Your bag is empty.",
        checkout: "Check out",
        continue: "Continue shopping",
        remove: "Remove",
        close: "Close bag",
        size: "Size",
        decrease: "Decrease quantity",
        increase: "Increase quantity",
        subtotal: "Subtotal",
        error: "Could not update the bag.",
        soldOut: "Sold out",
        added: "Added",
        add: "Add to bag",
        taxes: "Taxes and shipping calculated at checkout.",
      },
    };
    if (cfgEl) {
      try {
        const parsed = JSON.parse(cfgEl.textContent);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          const nextStrings =
            parsed.strings && typeof parsed.strings === "object" && !Array.isArray(parsed.strings)
              ? parsed.strings
              : {};
          cfg = { ...cfg, ...parsed, strings: { ...cfg.strings, ...nextStrings } };
        }
      } catch (e) {}
    }
    const shop = Boolean(window.Shopify) || cfg.shop === true;
    cfg.shopUrl = safeStoreHref(cfg.shopUrl, "/collections/all");
    if (shop) {
      cfg.checkoutUrl = "/checkout";
    } else {
      cfg.checkoutUrl = safeStoreHref("./cart.html", "./cart.html");
      cfg.shopUrl = safeStoreHref("./shop.html", "./shop.html");
    }
    const memKey = "roohi-bag";
    if (document.getElementById("PBarNextFrameWrapper") || document.getElementById("PBarNextFrame")) {
      document.documentElement.classList.add("has-preview-bar");
    }
    let cart = cfg.cart || { items: [], item_count: 0, total_price: 0 };
    if (!shop) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(memKey) || "null");
        if (saved && Array.isArray(saved.items)) cart = saved;
      } catch (e) {}
    }

    const money = (cents) => {
      const value = (Math.abs(Number(cents) || 0) / 100).toFixed(2);
      return (cfg.moneyFormat || "${{amount}}").replace(/\{\{\s*amount(?:_no_decimals)?\s*\}\}/, value);
    };

    const itemImage = (item) => {
      if (!item) return "";
      if (typeof item.image === "string") return item.image;
      if (item.image && item.image.url) return item.image.url;
      if (item.featured_image && item.featured_image.url) return item.featured_image.url;
      return "";
    };

    const productTitle = (item) => item.product_title || item.title || "";
    const variantTitle = (item) => {
      const t = item.variant_title || "";
      return t && t !== "Default Title" ? t : "";
    };

    const shopHref = () => safeStoreHref(cfg.shopUrl, shop ? "/collections/all" : "./shop.html");

    const createEmpty = (forBagPage) => {
      const wrap = document.createElement("div");
      wrap.className = forBagPage ? "bag__empty" : "cart-drawer__empty-wrap";
      const p = document.createElement("p");
      if (!forBagPage) p.className = "cart-drawer__empty";
      p.textContent = cfg.strings.empty;
      const a = document.createElement("a");
      a.className = "btn";
      a.setAttribute("href", shopHref());
      a.textContent = cfg.strings.continue;
      wrap.append(p, a);
      return wrap;
    };

    const ensureDrawer = () => {
      if (document.querySelector("[data-cart-drawer]")) return;
      const wrap = document.createElement("div");
      wrap.className = "cart-drawer";
      wrap.id = "CartDrawer";
      wrap.setAttribute("data-cart-drawer", "");
      wrap.setAttribute("aria-hidden", "true");
      wrap.innerHTML = `
        <button class="cart-drawer__scrim" type="button" tabindex="-1" data-cart-close aria-label="${escapeHtml(cfg.strings.close)}"></button>
        <div class="cart-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="CartDrawerTitle">
          <div class="cart-drawer__head">
            <p id="CartDrawerTitle">${escapeHtml(cfg.strings.bag)} <span class="cart-drawer__badge" data-cart-count-label>0</span></p>
            <button class="cart-drawer__close" type="button" data-cart-close aria-label="${escapeHtml(cfg.strings.close)}"><span></span></button>
          </div>
          <div class="cart-drawer__body" data-cart-lines></div>
          <div class="cart-drawer__foot" data-cart-foot hidden>
            <p class="bag__subtotal"><span>${escapeHtml(cfg.strings.subtotal)}</span> <span data-cart-subtotal></span></p>
            <p class="cart-drawer__note">${escapeHtml(cfg.strings.taxes)}</p>
            ${shop
              ? `<form action="/cart" method="post" target="_top" data-cart-checkout-form><button class="btn" type="submit" name="checkout" data-cart-checkout>${escapeHtml(cfg.strings.checkout)}</button></form>`
              : `<a class="btn" href="${escapeHtml(safeStoreHref(cfg.checkoutUrl, "./cart.html"))}" data-cart-checkout>${escapeHtml(cfg.strings.checkout)}</a>`}
          </div>
        </div>`;
      document.body.appendChild(wrap);
    };

    const createLine = (item) => {
      const key = isCartKey(item && item.key) ? item.key : "";
      const href = safeStoreHref(item && item.url, "#");
      const imgSrc = isAllowedMedia(itemImage(item)) ? itemImage(item) : "";
      const size = variantTitle(item);
      const qty = parseQty(item && item.quantity, 1) || 1;

      const article = document.createElement("article");
      article.className = "bag-line";
      if (key) {
        article.setAttribute("data-key", key);
        article.setAttribute("data-qty", String(qty));
      }

      const imgLink = document.createElement("a");
      imgLink.className = "bag-line__img";
      imgLink.setAttribute("href", href);
      if (imgSrc) {
        const img = document.createElement("img");
        img.src = imgSrc;
        img.alt = "";
        img.width = 96;
        img.height = 120;
        imgLink.appendChild(img);
      }

      const info = document.createElement("div");
      info.className = "bag-line__info";

      const row = document.createElement("div");
      row.className = "bag-line__row";
      const title = document.createElement("a");
      title.className = "bag-line__title";
      title.setAttribute("href", href);
      title.textContent = productTitle(item);
      const price = document.createElement("p");
      price.className = "bag-line__price";
      price.textContent = money(item && item.final_line_price);
      row.append(title, price);
      info.appendChild(row);

      if (size) {
        const opt = document.createElement("p");
        opt.className = "bag-line__opt";
        opt.textContent = size;
        info.appendChild(opt);
      }

      const unit = document.createElement("p");
      unit.className = "bag-line__unit";
      unit.textContent = money(item && item.price);
      info.appendChild(unit);

      const tools = document.createElement("div");
      tools.className = "bag-line__tools";
      const qtyWrap = document.createElement("div");
      qtyWrap.className = "qty";

      const dec = document.createElement("button");
      dec.type = "button";
      dec.setAttribute("data-cart-qty", "-1");
      if (key) dec.setAttribute("data-key", key);
      dec.setAttribute("aria-label", cfg.strings.decrease);
      dec.textContent = "-";

      const qtyVal = document.createElement("span");
      qtyVal.setAttribute("data-qty-value", "");
      qtyVal.textContent = String(qty);

      const inc = document.createElement("button");
      inc.type = "button";
      inc.setAttribute("data-cart-qty", "1");
      if (key) inc.setAttribute("data-key", key);
      inc.setAttribute("aria-label", cfg.strings.increase);
      inc.textContent = "+";
      qtyWrap.append(dec, qtyVal, inc);

      const remove = document.createElement("button");
      remove.className = "bag-line__remove";
      remove.type = "button";
      remove.setAttribute("data-cart-remove", "");
      if (key) remove.setAttribute("data-key", key);
      remove.setAttribute("aria-label", cfg.strings.remove);
      remove.innerHTML =
        '<svg class="icon-trash" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 7h8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M9.4 7V5.7A1.2 1.2 0 0 1 10.6 4.5h2.8A1.2 1.2 0 0 1 14.6 5.7V7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M7.2 7.2h9.6l-.5 12.2a1.5 1.5 0 0 1-1.5 1.4H9.2a1.5 1.5 0 0 1-1.5-1.4L7.2 7.2Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M10 11v6M14 11v6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
      tools.append(qtyWrap, remove);
      info.appendChild(tools);
      article.append(imgLink, info);
      return article;
    };

    const paint = (next) => {
      cart = next && typeof next === "object" ? next : { items: [], item_count: 0, total_price: 0 };
      if (!Array.isArray(cart.items)) cart.items = [];
      if (!shop) sessionStorage.setItem(memKey, JSON.stringify(cart));
      const count = parseQty(cart.item_count, 0);
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
      document.querySelectorAll("[data-cart-count-label]").forEach((el) => {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
      document.querySelectorAll("[data-cart-subtotal]").forEach((el) => {
        el.textContent = money(cart.total_price);
      });
      document.querySelectorAll("[data-cart-foot]").forEach((el) => {
        el.hidden = count === 0;
      });
      document.querySelectorAll("[data-cart-lines]").forEach((el) => {
        if (count) {
          el.replaceChildren(...cart.items.map(createLine));
          return;
        }
        el.replaceChildren(createEmpty(Boolean(el.closest("[data-bag]"))));
      });
    };

    const fetchJSON = async (url, options) => {
      if (
        typeof url !== "string" ||
        !url.startsWith("/") ||
        url.startsWith("//") ||
        url.includes("://") ||
        url.includes("?") ||
        !/\/cart(?:\/(?:add|change))?\.js$/.test(url)
      ) {
        throw new Error(cfg.strings.error);
      }
      const res = await fetch(url, {
        credentials: "same-origin",
        ...options,
      });
      let data = {};
      try {
        data = await res.json();
      } catch (e) {}
      if (!res.ok) {
        const err = new Error(data.description || data.message || cfg.strings.error);
        err.status = res.status;
        throw err;
      }
      return data;
    };

    const refresh = async () => {
      if (!shop) {
        paint(cart);
        return cart;
      }
      const next = await fetchJSON("/cart.js");
      paint(next);
      return next;
    };

    const memoryAdd = (payload) => {
      const id = String(payload.id);
      const qty = payload.quantity || 1;
      const existing = cart.items.find((item) => String(item.variant_id || item.id) === id);
      if (existing) {
        existing.quantity += qty;
        existing.final_line_price = existing.price * existing.quantity;
      } else {
        cart.items.push({
          key: `mem-${id}-${Date.now()}`,
          id,
          variant_id: id,
          product_title: String(payload.product_title || "Item").slice(0, 200),
          title: String(payload.product_title || "Item").slice(0, 200),
          variant_title: String(payload.variant_title || "").slice(0, 80),
          quantity: qty,
          price: payload.price || 0,
          final_line_price: (payload.price || 0) * qty,
          url: safeStoreHref(payload.url, "./product.html"),
          image: isAllowedMedia(payload.image) ? payload.image : "",
          handle: String(payload.handle || "").replace(/[^a-z0-9-]/gi, "").slice(0, 80),
          _variants: payload.variants || [],
        });
      }
      cart.item_count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
      cart.total_price = cart.items.reduce((sum, item) => sum + item.final_line_price, 0);
      paint(cart);
      return cart;
    };

    const add = async (payload) => {
      if (!shop) return memoryAdd(payload);
      const variantId = parseVariantId(payload && payload.id);
      const quantity = parseQty(payload && payload.quantity, 1) || 1;
      if (!variantId) {
        const err = new Error(cfg.strings.error);
        throw err;
      }
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const addUrl = `${root}cart/add.js`.replace(/\/{2,}(?=cart)/, "/");
      await fetchJSON(addUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
        }),
      });
      return refresh();
    };

    const change = async (key, quantity) => {
      if (!isCartKey(key)) return cart;
      const nextQty = parseQty(quantity, 0);
      if (!shop) {
        const item = cart.items.find((row) => row.key === key);
        if (!item) return cart;
        if (nextQty <= 0) cart.items = cart.items.filter((row) => row.key !== key);
        else {
          item.quantity = nextQty;
          item.final_line_price = item.price * nextQty;
        }
        cart.item_count = cart.items.reduce((sum, row) => sum + row.quantity, 0);
        cart.total_price = cart.items.reduce((sum, row) => sum + row.final_line_price, 0);
        paint(cart);
        return cart;
      }
      await fetchJSON("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: nextQty }),
      });
      return refresh();
    };

    const drawer = () => document.querySelector("[data-cart-drawer]");
    const setOpen = (open) => {
      const el = drawer();
      if (!el) return;
      el.classList.toggle("is-open", open);
      el.setAttribute("aria-hidden", open ? "false" : "true");
      document.documentElement.classList.toggle("bag-open", open);
      document.body.style.overflow = open ? "hidden" : "";
      document.querySelectorAll("[data-cart-open]").forEach((btn) => {
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      });
    };

    const popBag = () => {
      const icon = document.querySelector(".nav__cart");
      if (!icon) return;
      icon.classList.remove("is-pop");
      void icon.offsetWidth;
      icon.classList.add("is-pop");
    };

    ensureDrawer();
    paint(cart);

    let cartBusy = false;

    const goCheckout = () => {
      let dest = "/checkout";
      if (shop) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        dest = `${root}checkout`.replace(/\/{2,}/g, "/");
        if (!dest.startsWith("/") || dest.startsWith("//") || dest.includes("://")) dest = "/checkout";
      } else {
        dest = safeStoreHref(cfg.checkoutUrl, "./cart.html");
      }
      const win = window.top || window;
      try {
        win.location.assign(dest);
      } catch (e) {
        window.location.assign(dest);
      }
    };

    document.addEventListener("click", async (event) => {
      const checkoutBtn = event.target.closest("[data-cart-checkout]");
      if (checkoutBtn) {
        const form = checkoutBtn.closest("form[data-cart-checkout-form], form");
        if (shop && form && checkoutBtn.getAttribute("type") === "submit") {
          form.setAttribute("action", "/cart");
          form.setAttribute("method", "post");
          form.setAttribute("target", "_top");
          return;
        }
        event.preventDefault();
        goCheckout();
        return;
      }
      const openBtn = event.target.closest("[data-cart-open]");
      if (openBtn) {
        event.preventDefault();
        setOpen(true);
        return;
      }
      if (event.target.closest("[data-cart-close]")) {
        setOpen(false);
        return;
      }
      const qtyBtn = event.target.closest("[data-cart-qty]");
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        if (!isCartKey(key) || cartBusy) return;
        const line = cart.items.find((item) => item.key === key);
        if (!line) return;
        const next = parseQty(line.quantity, 1) + Number(qtyBtn.dataset.cartQty);
        if (next < 1) return;
        cartBusy = true;
        try {
          await change(key, next);
        } catch (e) {
        } finally {
          cartBusy = false;
        }
        return;
      }
      const removeBtn = event.target.closest("[data-cart-remove]");
      if (removeBtn) {
        const key = removeBtn.dataset.key;
        if (!isCartKey(key) || cartBusy) return;
        cartBusy = true;
        try {
          await change(key, 0);
        } catch (e) {
        } finally {
          cartBusy = false;
        }
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") setOpen(false);
    });

    document.querySelectorAll("[data-product] form, .product form").forEach((form) => {
      form.addEventListener("submit", async (event) => {
        const btn = form.querySelector("[type='submit']");
        const idInput = form.querySelector("[data-variant-id], input[name='id']");
        const checked = form.querySelector("[data-variant]:checked");
        const enabled = form.querySelector("[data-variant]:not(:disabled)");
        event.preventDefault();
        const raw = (checked && checked.value) || (idInput && idInput.value) || (enabled && enabled.value) || "";
        const id = parseVariantId(raw);
        if (!id) {
          if (btn) btn.textContent = cfg.strings.error;
          return;
        }
        const product = form.closest("[data-product], .product");
        const title = product && product.querySelector("h1") ? product.querySelector("h1").textContent.trim() : "";
        const priceText = product && product.querySelector(".product__price") ? product.querySelector(".product__price").textContent : "$0";
        const price = Math.round(parseFloat(priceText.replace(/[^0-9.]/g, "")) * 100) || 0;
        const img = product && product.querySelector(".product__stage img");
        const variants = [...form.querySelectorAll("[data-variant]")].map((input) => ({
          id: input.value,
          title: (input.nextElementSibling && input.nextElementSibling.textContent.trim()) || input.value,
          available: !input.disabled,
        }));
        if (cartBusy) return;
        if (btn) {
          btn.disabled = true;
          btn.textContent = cfg.strings.added;
        }
        cartBusy = true;
        try {
          await add({
            id,
            quantity: 1,
            product_title: title,
            variant_title: checked && checked.nextElementSibling ? checked.nextElementSibling.textContent.trim() : "",
            price,
            image: img ? img.currentSrc || img.src : "",
            url: location.pathname,
            handle: (location.pathname.split("/products/")[1] || "").replace(/\/$/, ""),
            variants,
          });
          popBag();
          setOpen(true);
        } catch (e) {
          const sold = e && /sold out/i.test(String(e.message || ""));
          if (btn) btn.textContent = sold ? cfg.strings.soldOut : cfg.strings.error;
          return;
        } finally {
          cartBusy = false;
        }
        window.setTimeout(() => {
          if (!btn) return;
          btn.disabled = false;
          btn.textContent = cfg.strings.add;
        }, 1400);
      });
    });
  };
  cartRoot();
})();
